// src/encounter/lib/to-lab-order.ts
// Map a picked lab CodeSearchResult (from the lab-order search) + its chosen sub-tests + a
// free-text note → a DS PlanOrder attached to a diagnosis on the AssessmentPlanCanvas, plus the
// structured side-map payload (the canvas PlanOrder is lossy {id,type,label}; codes + sub-tests +
// note live in OrderPayloadMap, keyed by orderId). The caller supplies the orderId (unique per
// attachment — the same panel can be ordered against more than one Dx, so the id is NOT derived
// from the code, which would collide).
//
// Unlike imaging (single study + note), a lab order carries a sub-test panel: the value-set tests
// the practitioner selected (OrderCoding[], possibly empty). The label is the lab's name ONLY
// (design 6.16): the test count lives on the row's detail line (lib/lab-plan-row.ts), not the label.
import type { CodeSearchResult, PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload, LabVendorRef, OrderCoding } from './order-payload';

export interface LabOrderResult {
  planOrder: PlanOrder;
  payload: Extract<CodedOrderPayload, { kind: 'lab' }>;
}

export function toLabOrder(
  result: CodeSearchResult,
  subTests: OrderCoding[],
  orderId: string,
  note?: string,
  /**
   * Deselected panel members (PROD-2632). INVARIANT: `undefined` = UNKNOWN and `[]` = KNOWN, nothing
   * omitted — keep the two distinct. The payload carries the key only when a list is passed, and
   * the save mapper sends the field only when it is present. Today's BE (Patient #988) reads a
   * missing field and `[]` the same way, but if it ever tells them apart (audit, say), collapsing
   * `undefined` into `[]` here would assert "nothing omitted" for orders we know nothing about.
   */
  omittedTests?: OrderCoding[],
  /** The performing lab vendor (PROD-2653). Omitted = not set; the payload then carries no key. */
  vendor?: LabVendorRef,
  /** The catalogue v2 panel version (PROD-2640). Omitted = a legacy pick; the payload carries no key. */
  panel?: { id: string; version?: number },
  /** Display-only facts for the plan row's detail line (never sent). */
  extra?: { specimen?: string; orderedAsOnePanel?: boolean },
  /**
   * Wire fields (PROD-2640): Bill to (Patient #1000) and the members already ordered by another lab
   * on the plan (Patient #1001). Each is carried only when passed; an empty `alreadyOrderedTests`
   * is carried as [] (known: none).
   */
  wire?: { billTo?: 'Patient' | 'Clinic'; alreadyOrderedTests?: OrderCoding[] },
): LabOrderResult {
  const display = result.display.trim();
  // Coding falls back code→display so a free-text/custom hit (no `code`) still carries a value.
  const code: OrderCoding = {
    code: result.code?.trim() || display,
    displayName: display,
    ...(result.system?.trim() ? { system: result.system.trim() } : {}),
  };
  const label = display;
  const specimen = extra?.specimen?.trim();
  const trimmedNote = note?.trim();
  return {
    // dxIds empty — the host attaches the target Dx when it appends the order (v2 PROD-1422).
    planOrder: { id: orderId, type: 'lab', label, dxIds: [] },
    payload: {
      kind: 'lab',
      code,
      subTests,
      ...(omittedTests ? { omittedTests } : {}),
      ...(vendor ? { vendor } : {}),
      ...(panel?.id
        ? { panelDefinitionId: panel.id, ...(typeof panel.version === 'number' ? { panelVersion: panel.version } : {}) }
        : {}),
      ...(wire?.billTo ? { billTo: wire.billTo } : {}),
      ...(wire?.alreadyOrderedTests ? { alreadyOrderedTests: wire.alreadyOrderedTests } : {}),
      ...(specimen ? { specimen } : {}),
      ...(extra?.orderedAsOnePanel ? { orderedAsOnePanel: true } : {}),
      ...(trimmedNote ? { note: trimmedNote } : {}),
    },
  };
}
